/**
 * Schlanker Date-Range-Picker mit deutscher Locale.
 * Popup wird via React Portal in document.body gerendert, damit es nicht
 * von einem `overflow-hidden` Parent-Container abgeschnitten wird.
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.tsx';

interface DateRange {
  min?: string;
  max?: string;
}

interface Props {
  value: DateRange;
  onChange: (v: DateRange) => void;
  className?: string;
  label?: string;
}

function today(): string {
  return new Date().toISOString().substring(0, 10);
}
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().substring(0, 10);
}
function startOfMonth(offsetMonths = 0): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offsetMonths);
  return d.toISOString().substring(0, 10);
}
function endOfMonth(offsetMonths = 0): string {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths + 1, 0);
  return d.toISOString().substring(0, 10);
}
function startOfWeek(): string {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d.toISOString().substring(0, 10);
}
function startOfYear(offsetYears = 0): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + offsetYears, 0, 1);
  return d.toISOString().substring(0, 10);
}
function endOfYear(offsetYears = 0): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + offsetYears, 11, 31);
  return d.toISOString().substring(0, 10);
}

const PRESETS: Array<{ label: string; range: () => DateRange }> = [
  { label: 'Heute', range: () => ({ min: today(), max: today() }) },
  { label: '7 Tage', range: () => ({ min: daysAgo(6), max: today() }) },
  { label: '30 Tage', range: () => ({ min: daysAgo(29), max: today() }) },
  { label: 'Diese Woche', range: () => ({ min: startOfWeek(), max: today() }) },
  { label: 'Dieser Monat', range: () => ({ min: startOfMonth(), max: today() }) },
  { label: 'Letzter Monat', range: () => ({ min: startOfMonth(-1), max: endOfMonth(-1) }) },
  { label: 'Dieses Jahr', range: () => ({ min: startOfYear(), max: today() }) },
  { label: 'Letztes Jahr', range: () => ({ min: startOfYear(-1), max: endOfYear(-1) }) },
];

export function DateRangePicker({ value, onChange, className = '', label }: Props) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 320,
  });

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({
        top: r.bottom + 4,
        left: Math.min(r.left, window.innerWidth - 340),
        width: Math.max(r.width, 320),
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const display = (() => {
    if (!value.min && !value.max) return 'Zeitraum wählen';
    if (value.min && value.max) {
      return `${fmtDateGerman(value.min)} – ${fmtDateGerman(value.max)}`;
    }
    if (value.min) return `ab ${fmtDateGerman(value.min)}`;
    return `bis ${fmtDateGerman(value.max!)}`;
  })();

  return (
    <div className={className}>
      {label && (
        <label className="block text-2xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400 mb-1">
          {label}
        </label>
      )}
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-brand-500 transition-colors"
      >
        <span
          className={
            value.min || value.max
              ? 'text-slate-900 dark:text-slate-100'
              : 'text-slate-400'
          }
        >
          {display}
        </span>
        <Icon name="clock" size={14} className="text-slate-400" />
      </button>

      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={() => setOpen(false)}
            />
            <div
              className="fixed z-[101] bg-white dark:bg-slate-900 rounded-xl shadow-elevated border border-slate-200 dark:border-slate-700 p-3 animate-slide-up"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="block text-2xs uppercase text-slate-500 mb-1">
                    Von
                  </label>
                  <input
                    type="date"
                    value={value.min ?? ''}
                    onChange={(e) =>
                      onChange({ ...value, min: e.target.value || undefined })
                    }
                    className="input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-2xs uppercase text-slate-500 mb-1">
                    Bis
                  </label>
                  <input
                    type="date"
                    value={value.max ?? ''}
                    onChange={(e) =>
                      onChange({ ...value, max: e.target.value || undefined })
                    }
                    className="input text-sm"
                  />
                </div>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-800 pt-2">
                <div className="text-2xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                  Schnellauswahl
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        onChange(p.range());
                        setOpen(false);
                      }}
                      className="text-left text-xs px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-800 mt-2 pt-2 flex justify-between">
                <button
                  type="button"
                  onClick={() => {
                    onChange({});
                    setOpen(false);
                  }}
                  className="text-2xs text-slate-500 hover:text-accent-600"
                >
                  Zurücksetzen
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="btn-primary text-xs"
                >
                  <Icon name="check" size={12} />
                  Übernehmen
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

function fmtDateGerman(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
